import { useCallback, useState, memo } from 'react';

import {
  AccountCircle,
  Apps,
  ArrowDropDown,
  ArrowDropUp,
  Brightness4,
  Brightness7,
} from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  Toolbar,
  Typography,
  Tooltip,
} from '@mui/material';
import { Link } from 'react-router-dom';

import useAuth, { roles } from 'contexts/AuthContext';
import { useActiveMenu } from 'hooks/context';

import geniusSportsLogoWhite from '../../assets/genius-sports-logo-white.svg';
import { useTheme } from '../../contexts/ThemeContext';
import { headerStyles, useHeaderStyles } from './Header.styles';
import { HeaderEnvMenu } from './HeaderEnvMenu';
import { HeaderLogoutMenu } from './HeaderLogoutMenu';

interface ThemeToggleButtonProps {
  themeMode: 'light' | 'dark';
  toggleTheme: () => void;
}

const ThemeToggleButton = memo(({ themeMode, toggleTheme }: ThemeToggleButtonProps) => (
  <Tooltip title={`Switch to ${themeMode === 'light' ? 'dark' : 'light'} mode`}>
    <IconButton
      onClick={toggleTheme}
      color="inherit"
      sx={headerStyles.iconButton}
      size="large"
      data-testid="theme-toggle"
      aria-label="toggle theme"
    >
      {themeMode === 'light' ? <Brightness4 /> : <Brightness7 />}
    </IconButton>
  </Tooltip>
));

ThemeToggleButton.displayName = 'ThemeToggleButton';

const Header = () => {
  const styles = useHeaderStyles();
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [envEl, setEnvEl] = useState(null);
  const { logout, user } = useAuth();
  const [activeMenu, setActiveMenu] = useActiveMenu();
  const { themeMode, toggleTheme } = useTheme();

  const handleNavigation = (e, menuName) => {
    if (e.ctrlKey || e.metaKey) {
      return;
    }

    setActiveMenu(menuName);
  };

  const handleEnvElClick = useCallback(event => {
    setEnvEl(event.currentTarget);
  }, []);

  const handleClick = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setEnvEl(null);
  }, []);

  const handleLogoutButtonClick = async () => {
    setLogoutLoading(true);
    logout();
    setLogoutLoading(false);
  };

  return (
    <AppBar sx={headerStyles.root} data-testid="app-header">
      <Toolbar>
        <img
          width={32}
          height={32}
          alt="Genius Sports"
          src={geniusSportsLogoWhite}
          data-testid="gs-logo"
        />
        <Divider sx={headerStyles.divider} />
        <Typography fontSize={20} variant="caption" data-testid="product-name">
          {import.meta.env.VITE_ENVIRONMENT}
        </Typography>
        <div style={{ margin: '0 10px', display: 'flex', flexDirection: 'row', gap: '10px' }}>
          <Link
            to="/competition-hierarchy"
            onClick={e => handleNavigation(e, 'competition-hierarchy')}
            style={{ textDecoration: 'none' }}
          >
            <Button
              variant={activeMenu === 'competition-hierarchy' ? 'contained' : 'outlined'}
              className={styles.navigationLink}
            >
              Competition Hierarchy
            </Button>
          </Link>

          {user.role === roles['fixture-api:user-management'] && (
            <Link
              to="/settings"
              onClick={e => handleNavigation(e, 'settings')}
              style={{ textDecoration: 'none' }}
            >
              <Button
                variant={activeMenu === 'settings' ? 'contained' : 'outlined'}
                className={styles.navigationLink}
              >
                Settings
              </Button>
            </Link>
          )}

          <Link
            to="/proposals"
            onClick={e => handleNavigation(e, 'proposals')}
            style={{ textDecoration: 'none' }}
          >
            <Button
              variant={activeMenu === 'proposals' ? 'contained' : 'outlined'}
              className={styles.navigationLink}
            >
              Proposals
            </Button>
          </Link>

          {user.scope.includes('matching-api:read') && (
            <Link
              to="/matching"
              onClick={e => handleNavigation(e, 'matching')}
              style={{ textDecoration: 'none' }}
            >
              <Button
                variant={activeMenu === 'matching' ? 'contained' : 'outlined'}
                className={styles.navigationLink}
              >
                Matching
              </Button>
            </Link>
          )}

          <Link
            to="/coverage"
            onClick={e => handleNavigation(e, 'coverage')}
            style={{ textDecoration: 'none' }}
          >
            <Button
              variant={activeMenu === 'coverage' ? 'contained' : 'outlined'}
              className={styles.navigationLink}
            >
              Coverage
            </Button>
          </Link>

          {user.role === roles['fixture-api:user-management'] && (
            <Link
              to="/report"
              onClick={e => handleNavigation(e, 'report')}
              style={{ textDecoration: 'none' }}
            >
              <Button
                variant={activeMenu === 'report' ? 'contained' : 'outlined'}
                className={styles.navigationLink}
              >
                Report
              </Button>
            </Link>
          )}
        </div>

        <Box sx={headerStyles.middleBreak} />
        <ThemeToggleButton themeMode={themeMode} toggleTheme={toggleTheme} />
        <IconButton
          disableRipple
          data-testid="envEl-menu"
          sx={headerStyles.iconButton}
          onClick={handleEnvElClick}
          aria-controls="envEl-menu"
          aria-haspopup="true"
          size="large"
        >
          <Apps />
        </IconButton>
        <HeaderEnvMenu envEl={envEl} handleClose={handleClose} />
        <Divider sx={headerStyles.divider} />
        <Button
          disableRipple
          data-testid="user-menu"
          aria-controls="user-main-action-menu"
          aria-haspopup="true"
          onClick={handleClick}
          sx={headerStyles.profileButton}
        >
          <AccountCircle sx={headerStyles.accountButton} />
          <Box sx={headerStyles.textBox}>
            <Typography
              variant="subtitle1"
              data-testid="user-role"
              sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
            >
              {user.role}
            </Typography>
            <Typography variant="body1" sx={headerStyles.userName} data-testid="username">
              {user.email.slice(0, user.email.indexOf('@')).replace('.', ' ')}
            </Typography>
          </Box>
          {anchorEl ? (
            <ArrowDropUp sx={headerStyles.arrowIcon} />
          ) : (
            <ArrowDropDown sx={headerStyles.arrowIcon} />
          )}
        </Button>
        <HeaderLogoutMenu
          anchorEl={anchorEl}
          handleClose={handleClose}
          handleLogoutButtonClick={handleLogoutButtonClick}
          disabled={logoutLoading}
        />
      </Toolbar>
    </AppBar>
  );
};

export default Header;
